<template>
  <div class="mini-tab-content">
    <div
      v-if="loading"
      class="interc-loading"
    >
      <span>Carregando...</span>
    </div>

    <template v-else>
      <div class="interc-toolbar">
        <span class="interc-toolbar__title">
          <feather-icon
            icon="DropletIcon"
            size="13"
            class="mr-25 text-primary"
          />
          {{ activeCount ? activeCount + ' prescrição(ões)' : 'Nenhuma prescrição' }}
        </span>
        <button
          class="btn btn-sm btn-primary"
          @click="openModal"
        >
          <feather-icon
            icon="PlusIcon"
            size="13"
            class="mr-25"
          />
          Nova Prescrição
        </button>
      </div>

      <div
        v-if="!prescriptions.length"
        class="interc-empty"
      >
        <feather-icon
          icon="DropletIcon"
          size="26"
        />
        <p>Nenhuma prescrição registrada.</p>
      </div>

      <!-- Grupos de prescrição (agrupados por batch_id) -->
      <div
        v-if="prescriptions.length"
        class="interc-list"
      >
        <div
          v-for="(group, gi) in prescriptionGroups"
          :key="gi"
          :class="['rx-group', { 'rx-group--cancelled': isGroupFullyCancelled(group) }]"
        >
          <!-- Itens do grupo -->
          <div
            v-for="p in group"
            :key="p.id"
            class="rx-item"
            :class="{ 'rx-item--cancelled': p.cancelled_by_user_id }"
          >
            <div class="rx-item__top">
              <span class="rx-item__med">
                <feather-icon
                  icon="DropletIcon"
                  size="12"
                  class="mr-25"
                />
                {{ p.medication }}
              </span>
              <div class="rx-item__actions">
                <button
                  v-if="!p.cancelled_by_user_id"
                  class="rx-apply-btn"
                  title="Registrar aplicação"
                  @click="openApplyModal(p)"
                >
                  <feather-icon
                    icon="CheckIcon"
                    size="11"
                    class="mr-25"
                  />
                  {{ p.applications && p.applications.length ? 'Nova Aplicação' : 'Dar Baixa' }}
                </button>
                <button
                  v-if="!p.cancelled_by_user_id"
                  class="interc-item__del"
                  title="Cancelar este medicamento"
                  @click="cancelPrescription(p.id)"
                >
                  <feather-icon
                    icon="XIcon"
                    size="13"
                  />
                </button>
              </div>
            </div>

            <div
              v-if="posology(p)"
              class="rx-item__posology"
            >
              {{ posology(p) }}
            </div>

            <!-- Aplicações registradas -->
            <div
              v-if="p.applications && p.applications.length"
              class="rx-applied-list"
            >
              <div
                v-for="app in p.applications"
                :key="app.id"
                class="rx-applied-item"
              >
                <feather-icon
                  icon="CheckCircleIcon"
                  size="11"
                  class="mr-25 text-success"
                />
                <span>
                  Aplicado por
                  <strong>{{ applierName(app) }}</strong>
                  · {{ app.applied_at | formatDate }}
                  <span
                    v-if="app.lot"
                    class="rx-applied-meta"
                  >· Lote: {{ app.lot }}</span>
                  <span
                    v-if="app.quantity_applied"
                    class="rx-applied-meta"
                  >· {{ app.quantity_applied }}</span>
                </span>
              </div>
            </div>

            <div
              v-if="p.cancelled_by_user_id"
              class="interc-item__cancelled-row"
            >
              <feather-icon
                icon="XCircleIcon"
                size="11"
                class="mr-25"
              />
              Cancelado por
              <strong class="ml-25">{{ p.cancelled_by ? p.cancelled_by.name : '—' }}</strong>
              · {{ p.cancelled_at | formatDate }}
            </div>
          </div>

          <!-- Rodapé do grupo: notes + meta + botão cancelar tudo -->
          <div class="rx-group__footer">
            <p
              v-if="group[0].notes"
              class="rx-item__notes"
            >
              {{ group[0].notes }}
            </p>
            <div class="rx-group__meta">
              <div class="interc-item__meta">
                <feather-icon
                  icon="UserIcon"
                  size="11"
                />
                <span>{{ group[0].user ? group[0].user.name : '—' }}</span>
                <span class="interc-item__dot" />
                <feather-icon
                  icon="ClockIcon"
                  size="11"
                />
                <span>{{ group[0].created_at | formatDate }}</span>
              </div>
              <button
                v-if="!isGroupFullyCancelled(group) && group[0].batch_id && group.length > 1"
                class="btn btn-sm btn-outline-danger rx-group__cancel-all"
                @click="cancelGroup(group)"
              >
                <feather-icon
                  icon="Trash2Icon"
                  size="12"
                  class="mr-25"
                />
                Cancelar todos
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Modal de registrar aplicação -->
    <b-modal
      v-model="showApplyModal"
      title="Registrar Aplicação"
      :title-class="'font-weight-bold'"
      size="md"
      centered
      no-close-on-backdrop
      @hidden="resetApplyForm"
    >
      <!-- Destaque do medicamento alvo -->
      <div
        v-if="applyTarget"
        class="rx-apply-target mb-2"
      >
        <feather-icon
          icon="DropletIcon"
          size="13"
          class="mr-25 text-primary"
        />
        <strong>{{ applyTarget.medication }}</strong>
        <span
          v-if="posology(applyTarget)"
          class="rx-apply-posology"
        >{{ posology(applyTarget) }}</span>
      </div>

      <!-- Aplicado por (typeahead) -->
      <div class="form-group mb-1">
        <label class="interc-label"><span class="required">*</span> Aplicado por</label>
        <div class="rx-search">
          <input
            v-model="applyForm.applied_by_query"
            class="form-control"
            type="text"
            placeholder="Buscar por nome ou digitar livremente..."
            autocomplete="off"
            @input="onUserInput"
            @focus="onUserInput"
            @keydown.esc="applyForm._showUserResults = false"
          >
          <div
            v-if="applyForm._showUserResults"
            class="rx-results"
          >
            <button
              v-for="u in filteredUsers"
              :key="u.id"
              type="button"
              class="rx-result"
              @click="selectUser(u)"
            >
              <span class="rx-result__name">{{ u.name }}</span>
            </button>
            <button
              v-if="!filteredUsers.length && applyForm.applied_by_query.length >= 2"
              type="button"
              class="rx-result"
              @click="useFreetextUser"
            >
              <span class="rx-result__name">
                Usar "{{ applyForm.applied_by_query }}" como nome livre
              </span>
            </button>
            <div
              v-if="loadingUsers"
              class="rx-results__empty"
            >
              Carregando...
            </div>
          </div>
        </div>
        <div
          v-if="applyForm.applied_by_user_id || applyForm.applied_by_name"
          class="mt-25"
        >
          <span class="rx-search__badge">
            <feather-icon
              icon="CheckCircleIcon"
              size="11"
              class="mr-25"
            />
            {{ applyForm.applied_by_query }}
          </span>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <div class="form-group mb-1">
            <label class="interc-label">Lote</label>
            <input
              v-model="applyForm.lot"
              class="form-control"
              type="text"
              placeholder="Ex.: 2024A001"
              maxlength="100"
            >
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-1">
            <label class="interc-label">Quantidade aplicada</label>
            <input
              v-model="applyForm.quantity_applied"
              class="form-control"
              type="text"
              placeholder="Padrão: dose prescrita"
              maxlength="100"
            >
          </div>
        </div>
      </div>

      <div class="form-group mb-1">
        <label class="interc-label">Data/hora da aplicação</label>
        <input
          v-model="applyForm.applied_at"
          class="form-control"
          type="datetime-local"
        >
      </div>

      <div class="form-group mb-0">
        <label class="interc-label">Observações</label>
        <textarea
          v-model="applyForm.notes"
          class="form-control"
          rows="2"
          placeholder="Observações sobre a aplicação..."
          maxlength="2000"
        />
      </div>

      <template #modal-footer>
        <button
          class="btn btn-sm btn-outline-secondary mr-1"
          @click="showApplyModal = false"
        >
          Cancelar
        </button>
        <button
          class="btn btn-sm btn-success"
          :disabled="savingApply || !applyFormValid"
          @click="saveApplication"
        >
          <span
            v-if="savingApply"
            class="spinner-border spinner-border-sm mr-25"
          />
          <feather-icon
            v-else
            icon="CheckIcon"
            size="13"
            class="mr-25"
          />
          Confirmar Aplicação
        </button>
      </template>
    </b-modal>

    <!-- Modal de nova prescrição -->
    <b-modal
      v-model="showModal"
      title="Nova Prescrição"
      :title-class="'font-weight-bold'"
      size="lg"
      centered
      no-close-on-backdrop
      @hidden="resetForm"
    >
      <!-- Linhas de medicamentos -->
      <div
        v-for="(item, idx) in form.items"
        :key="idx"
        class="rx-form-item"
      >
        <div class="rx-form-item__header">
          <span class="rx-form-item__num">Medicamento {{ form.items.length > 1 ? idx + 1 : '' }}</span>
          <button
            v-if="form.items.length > 1"
            type="button"
            class="btn btn-sm btn-outline-danger rx-form-item__remove"
            @click="removeItem(idx)"
          >
            <feather-icon
              icon="TrashIcon"
              size="12"
            />
          </button>
        </div>

        <div class="row">
          <div class="col-12">
            <div class="form-group mb-1">
              <label class="interc-label"><span class="required">*</span> Medicamento / Material</label>
              <div class="rx-search">
                <input
                  v-model="item.medication"
                  class="form-control"
                  type="text"
                  placeholder="Buscar no catálogo ou digitar livremente..."
                  autocomplete="off"
                  maxlength="255"
                  @input="onItemMedInput(item)"
                  @focus="onItemMedInput(item)"
                  @keydown.esc="item._showResults = false"
                >
                <span
                  v-if="item._searching"
                  class="rx-search__spinner"
                >
                  <span class="spinner-border spinner-border-sm text-primary" />
                </span>
                <span
                  v-else-if="item.material_id"
                  class="rx-search__badge"
                  title="Selecionado do catálogo"
                >
                  <feather-icon
                    icon="CheckCircleIcon"
                    size="13"
                    class="mr-25"
                  />catálogo
                </span>

                <div
                  v-if="item._showResults"
                  class="rx-results"
                >
                  <button
                    v-for="m in item._results"
                    :key="m.id"
                    type="button"
                    class="rx-result"
                    @click="selectItemMaterial(item, m)"
                  >
                    <span
                      v-if="m.code"
                      class="rx-result__code"
                    >{{ m.code }}</span>
                    <span class="rx-result__name">{{ m.name }}</span>
                    <span
                      v-if="m.controlled"
                      class="rx-result__tag"
                    >controlado</span>
                  </button>
                  <div
                    v-if="!item._results.length && !item._searching"
                    class="rx-results__empty"
                  >
                    Nada no catálogo — você pode digitar livremente.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-4 col-sm-6">
            <div class="form-group mb-1">
              <label class="interc-label">Dose</label>
              <input
                v-model="item.dose"
                class="form-control"
                type="text"
                placeholder="Ex.: 500 mg, 10 mg/kg"
                maxlength="100"
              >
            </div>
          </div>

          <div class="col-md-4 col-sm-6">
            <div class="form-group mb-1">
              <label class="interc-label">Via</label>
              <select
                v-model="item.route"
                class="form-control"
              >
                <option value="">
                  —
                </option>
                <option
                  v-for="r in routeOptions"
                  :key="r"
                >
                  {{ r }}
                </option>
              </select>
            </div>
          </div>

          <div class="col-md-4 col-sm-12">
            <div class="form-group mb-1">
              <label class="interc-label">Frequência</label>
              <input
                v-model="item.frequency"
                class="form-control"
                type="text"
                placeholder="Ex.: 8/8h, dose única, BIC"
                maxlength="100"
              >
            </div>
          </div>
        </div>

        <hr
          v-if="idx < form.items.length - 1"
          class="rx-form-divider"
        >
      </div>

      <!-- Botão adicionar medicamento -->
      <div class="mb-1">
        <button
          type="button"
          class="btn btn-sm btn-outline-primary"
          @click="addItem"
        >
          <feather-icon
            icon="PlusIcon"
            size="13"
            class="mr-25"
          />
          Adicionar medicamento
        </button>
      </div>

      <!-- Observação compartilhada -->
      <div class="form-group mb-0">
        <label class="interc-label">Observação</label>
        <textarea
          v-model="form.notes"
          class="form-control"
          rows="2"
          placeholder="Observações da prescrição..."
          maxlength="2000"
        />
      </div>

      <template #modal-footer>
        <button
          class="btn btn-sm btn-outline-secondary mr-1"
          @click="showModal = false"
        >
          Cancelar
        </button>
        <button
          class="btn btn-sm btn-primary"
          :disabled="saving || !medicationFilled"
          @click="save"
        >
          <span
            v-if="saving"
            class="spinner-border spinner-border-sm mr-25"
          />
          <feather-icon
            v-else
            icon="SaveIcon"
            size="13"
            class="mr-25"
          />
          Registrar
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import involvedPanelMixin from './involvedPanelMixin'

const ROUTE_OPTIONS = ['EV', 'IM', 'SC', 'VO', 'SL', 'IO', 'Inalatória', 'Retal', 'Tópica', 'Outra']

export default {
  name: 'PrescriptionsPanel',
  mixins: [involvedPanelMixin],
  data() {
    return {
      prescriptions: [],
      loading: false,
      showModal: false,
      saving: false,
      form: this.emptyForm(),
      routeOptions: ROUTE_OPTIONS,
      showApplyModal: false,
      applyTarget: null,
      savingApply: false,
      users: [],
      loadingUsers: false,
      applyForm: {
        applied_by_query: '',
        applied_by_user_id: null,
        applied_by_name: '',
        lot: '',
        quantity_applied: '',
        applied_at: '',
        notes: '',
        _showUserResults: false,
      },
    }
  },
  computed: {
    prescriptionGroups() {
      const groups = {}
      const order = []
      this.prescriptions.forEach(p => {
        const key = p.batch_id || `_${p.id}`
        if (!groups[key]) {
          groups[key] = []
          order.push(key)
        }
        groups[key].push(p)
      })
      return order.map(k => groups[k])
    },
    activeCount() {
      return this.prescriptionGroups.filter(
        group => group.some(p => !p.cancelled_by_user_id)
      ).length
    },
    medicationFilled() {
      return this.form.items.some(i => i.medication && i.medication.trim())
    },
    filteredUsers() {
      const q = (this.applyForm.applied_by_query || '').trim().toLowerCase()
      if (!q || q.length < 2) return this.users.slice(0, 10)
      return this.users
        .filter(u => u.name && u.name.toLowerCase().includes(q))
        .slice(0, 10)
    },
    applyFormValid() {
      return !!(this.applyForm.applied_by_user_id || (this.applyForm.applied_by_name && this.applyForm.applied_by_name.trim()))
    },
  },
  mounted() {
    this.fetchPrescriptions()
    Echo.private(`occurrence.${this.occurrenceId}`)
      .listen('.PrescriptionApplied', (data) => {
        const app = data.application
        if (!app) return
        const presc = this.prescriptions.find(p => p.id === app.occurrence_prescription_id)
        if (presc) {
          if (!presc.applications) this.$set(presc, 'applications', [])
          const exists = presc.applications.find(a => a.id === app.id)
          if (!exists) presc.applications.push(app)
        }
      })
  },
  beforeDestroy() {
    Echo.private(`occurrence.${this.occurrenceId}`)
      .stopListening('.PrescriptionApplied')
  },
  methods: {
    emptyForm() {
      return { notes: '', items: [this.emptyItem()] }
    },
    emptyItem() {
      return {
        medication: '', material_id: null, dose: '', route: '', frequency: '',
        _results: [], _searching: false, _showResults: false, _timer: null,
      }
    },
    addItem() {
      this.form.items.push(this.emptyItem())
    },
    removeItem(idx) {
      if (this.form.items.length <= 1) return
      const item = this.form.items[idx]
      if (item._timer) clearTimeout(item._timer)
      this.form.items.splice(idx, 1)
    },
    onItemMedInput(item) {
      item.material_id = null
      const q = (item.medication || '').trim()
      if (item._timer) clearTimeout(item._timer)
      if (q.length < 2) {
        item._results = []
        item._showResults = false
        return
      }
      item._timer = setTimeout(() => this.runItemSearch(item, q), 300)
    },
    async runItemSearch(item, q) {
      item._searching = true
      item._showResults = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}materials/search`, { params: { q } })
        item._results = res.data.data || []
      } catch (e) {
        item._results = []
      } finally {
        item._searching = false
      }
    },
    selectItemMaterial(item, m) {
      item.medication = m.name
      item.material_id = m.id
      item._results = []
      item._showResults = false
    },
    posology(p) {
      return [p.dose, p.route, p.frequency].filter(Boolean).join(' · ')
    },
    isGroupFullyCancelled(group) {
      return group.every(p => !!p.cancelled_by_user_id)
    },
    openModal() {
      this.form = this.emptyForm()
      this.showModal = true
    },
    resetForm() {
      this.form = this.emptyForm()
    },
    async fetchPrescriptions() {
      this.loading = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}occurrence_prescriptions`, {
          params: {
            occurrence_id: Number(this.occurrenceId),
            involved_id: this.involvedId ? Number(this.involvedId) : undefined,
          },
        })
        this.prescriptions = res.data.data || []
      } catch (e) {
        this.prescriptions = []
        this.$message('Erro', 'Não foi possível carregar as prescrições.', 'error')
      } finally {
        this.loading = false
      }
    },
    async save() {
      const validItems = this.form.items.filter(i => i.medication && i.medication.trim())
      if (!validItems.length) return
      this.saving = true
      try {
        const payload = {
          occurrence_id: Number(this.occurrenceId),
          involved_id: this.involvedId ? Number(this.involvedId) : null,
          notes: this.form.notes || null,
          items: validItems.map(i => ({
            medication: i.medication.trim(),
            material_id: i.material_id || null,
            dose: i.dose || null,
            route: i.route || null,
            frequency: i.frequency || null,
          })),
        }
        const res = await this.$http.post(`${this.$store.state.api}occurrence_prescriptions`, payload)
        const saved = res.data.data || []
        this.prescriptions.unshift(...saved)
        this.showModal = false
        this.$message('Prescrição registrada', 'Prescrição salva com sucesso.', 'success')
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : 'Erro ao registrar prescrição.'
        this.$message('Erro', msg, 'error')
      } finally {
        this.saving = false
      }
    },
    async cancelPrescription(id) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Este medicamento ficará marcado como cancelado no histórico. Deseja continuar?',
        { title: 'Cancelar medicamento', okTitle: 'Sim, cancelar', cancelTitle: 'Voltar', okVariant: 'danger', centered: true, size: 'sm' },
      )
      if (!confirmed) return
      try {
        const res = await this.$http.delete(`${this.$store.state.api}occurrence_prescriptions/${id}`)
        const updated = res.data.data || res.data
        const idx = this.prescriptions.findIndex(p => p.id === id)
        if (idx !== -1) this.$set(this.prescriptions, idx, updated)
      } catch (e) {
        this.$message('Erro', 'Erro ao cancelar medicamento.', 'error')
      }
    },
    applierName(app) {
      return (app.applied_by && app.applied_by.name) || app.applied_by_name || '—'
    },
    nowDatetimeLocal() {
      const d = new Date()
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    openApplyModal(p) {
      this.applyTarget = p
      this.applyForm = {
        applied_by_query: '',
        applied_by_user_id: null,
        applied_by_name: '',
        lot: '',
        quantity_applied: p.dose || '',
        applied_at: this.nowDatetimeLocal(),
        notes: '',
        _showUserResults: false,
      }
      this.showApplyModal = true
      this.loadUsers()
    },
    resetApplyForm() {
      this.applyTarget = null
      this.applyForm = {
        applied_by_query: '',
        applied_by_user_id: null,
        applied_by_name: '',
        lot: '',
        quantity_applied: '',
        applied_at: '',
        notes: '',
        _showUserResults: false,
      }
    },
    async loadUsers() {
      if (this.users.length > 0) return
      this.loadingUsers = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}users`, { params: { paginated: false } })
        this.users = (res.data.data || res.data || []).filter(u => u && u.name)
      } catch (e) {
        this.users = []
      } finally {
        this.loadingUsers = false
      }
    },
    onUserInput() {
      this.applyForm.applied_by_user_id = null
      this.applyForm.applied_by_name = ''
      const q = (this.applyForm.applied_by_query || '').trim()
      this.applyForm._showUserResults = q.length >= 1
    },
    selectUser(u) {
      this.applyForm.applied_by_user_id = u.id
      this.applyForm.applied_by_name = ''
      this.applyForm.applied_by_query = u.name
      this.applyForm._showUserResults = false
    },
    useFreetextUser() {
      this.applyForm.applied_by_name = this.applyForm.applied_by_query
      this.applyForm.applied_by_user_id = null
      this.applyForm._showUserResults = false
    },
    async saveApplication() {
      if (!this.applyFormValid || !this.applyTarget) return
      this.savingApply = true
      try {
        const payload = {
          occurrence_prescription_id: this.applyTarget.id,
          applied_by_user_id: this.applyForm.applied_by_user_id || null,
          applied_by_name: this.applyForm.applied_by_user_id ? null : (this.applyForm.applied_by_name || null),
          lot: this.applyForm.lot || null,
          quantity_applied: this.applyForm.quantity_applied || null,
          applied_at: this.applyForm.applied_at || null,
          notes: this.applyForm.notes || null,
        }
        const res = await this.$http.post(`${this.$store.state.api}occurrence_prescription_applications`, payload)
        const app = res.data.data
        if (!this.applyTarget.applications) this.$set(this.applyTarget, 'applications', [])
        this.applyTarget.applications.push(app)
        this.showApplyModal = false
        this.$message('Aplicação registrada', 'Baixa registrada com sucesso.', 'success')
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : 'Erro ao registrar aplicação.'
        this.$message('Erro', msg, 'error')
      } finally {
        this.savingApply = false
      }
    },
    async cancelGroup(group) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Todos os medicamentos desta prescrição serão cancelados. Deseja continuar?',
        { title: 'Cancelar prescrição', okTitle: 'Sim, cancelar tudo', cancelTitle: 'Voltar', okVariant: 'danger', centered: true, size: 'sm' },
      )
      if (!confirmed) return
      const batchId = group[0].batch_id
      try {
        const res = await this.$http.delete(`${this.$store.state.api}occurrence_prescriptions/batch/${batchId}`)
        const updated = res.data.data || []
        updated.forEach(u => {
          const idx = this.prescriptions.findIndex(p => p.id === u.id)
          if (idx !== -1) this.$set(this.prescriptions, idx, u)
        })
      } catch (e) {
        this.$message('Erro', 'Erro ao cancelar prescrição.', 'error')
      }
    },
  },
}
</script>

<style scoped>
/* --- Card de grupo --- */
.rx-group {
  border: 1px solid #ebe9f1;
  border-left: 3px solid #7367f0;
  border-radius: 0 6px 6px 0;
  background: #fff;
  margin-bottom: 10px;
  overflow: hidden;
}
.rx-group--cancelled {
  border-left-color: #b0b0b0;
  background: rgba(0, 0, 0, 0.03);
  opacity: 0.7;
}

/* --- Item individual dentro do grupo --- */
.rx-item {
  padding: 8px 12px 4px;
  border-bottom: 1px dashed #f3f2f7;
}
.rx-item:last-child {
  border-bottom: none;
}
.rx-item--cancelled .rx-item__med {
  text-decoration: line-through;
  color: #b9b9c3;
}
.rx-item__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.rx-item__med {
  display: inline-flex;
  align-items: center;
  font-size: 13.5px;
  font-weight: 700;
  color: #5e5873;
}
.rx-item__posology {
  font-size: 12.5px;
  color: #7367f0;
  font-weight: 600;
  margin-top: 2px;
}

/* --- Rodapé do grupo --- */
.rx-group__footer {
  padding: 6px 12px 8px;
  background: rgba(115, 103, 240, 0.03);
}
.rx-item__notes {
  font-size: 12px;
  color: #6e6b7b;
  margin: 0 0 4px;
  line-height: 1.45;
  white-space: pre-wrap;
}
.rx-group__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.rx-group__cancel-all {
  font-size: 11px;
  padding: 2px 8px;
}

/* --- Formulário de item no modal --- */
.rx-form-item {
  margin-bottom: 4px;
}
.rx-form-item__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.rx-form-item__num {
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #7367f0;
}
.rx-form-item__remove {
  padding: 2px 6px;
  font-size: 11px;
}
.rx-form-divider {
  border-color: #ebe9f1;
  margin: 10px 0 12px;
}

/* --- Typeahead --- */
.rx-search {
  position: relative;
}
.rx-search__spinner {
  position: absolute;
  right: 10px;
  top: 9px;
}
.rx-search__badge {
  position: absolute;
  right: 8px;
  top: 7px;
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #28c76f;
  background: rgba(40, 199, 111, 0.12);
  border-radius: 5px;
  padding: 2px 6px;
}
.rx-results {
  position: absolute;
  z-index: 30;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  max-height: 260px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(34, 41, 47, 0.12);
}
.rx-result {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: none;
  border-bottom: 1px solid #f3f2f7;
  background: #fff;
  padding: 8px 12px;
  cursor: pointer;
}
.rx-result:last-child { border-bottom: none; }
.rx-result:hover { background: #f8f7ff; }
.rx-result__code {
  flex-shrink: 0;
  font-weight: 800;
  font-size: 11.5px;
  color: #7367f0;
  background: #f0eefe;
  border-radius: 5px;
  padding: 2px 7px;
  min-width: 70px;
  text-align: center;
}
.rx-result__name { flex: 1; font-size: 12.5px; color: #5e5873; line-height: 1.3; }
.rx-result__tag { flex-shrink: 0; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; color: #ea5455; }
.rx-results__empty { padding: 12px; font-size: 12px; color: #b9b9c3; text-align: center; }

/* --- Ações do item --- */
.rx-item__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

/* --- Botão Dar Baixa --- */
.rx-apply-btn {
  display: inline-flex;
  align-items: center;
  padding: 2px 9px;
  font-size: 11px;
  font-weight: 600;
  border: 1.5px solid #28c76f;
  border-radius: 20px;
  background: transparent;
  color: #28c76f;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;
}
.rx-apply-btn:hover {
  background: #28c76f;
  color: #fff;
}

/* --- Lista de aplicações no item --- */
.rx-applied-list {
  margin-top: 5px;
  margin-bottom: 3px;
}
.rx-applied-item {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  font-size: 11.5px;
  color: #28c76f;
  font-weight: 600;
  line-height: 1.4;
  margin-bottom: 2px;
}
.rx-applied-meta {
  font-weight: 400;
  color: #6e6b7b;
}

/* --- Modal de aplicação: destaque do medicamento --- */
.rx-apply-target {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 12px;
  background: #f8f7ff;
  border: 1px solid #ebe9f1;
  border-left: 3px solid #7367f0;
  border-radius: 0 6px 6px 0;
  font-size: 13px;
  color: #5e5873;
}
.rx-apply-posology {
  font-size: 12px;
  font-weight: 600;
  color: #7367f0;
}

/* --- Dark layout --- */
.dark-layout .rx-group { background: #283046; border-color: #3b4253; border-left-color: #7367f0; }
.dark-layout .rx-group--cancelled { background: rgba(0,0,0,.18); }
.dark-layout .rx-item { border-bottom-color: #3b4253; }
.dark-layout .rx-item__med { color: #d0d2d6; }
.dark-layout .rx-item--cancelled .rx-item__med { color: #6e6b7b; }
.dark-layout .rx-item__posology { color: #a99ff7; }
.dark-layout .rx-group__footer { background: rgba(115,103,240,.05); }
.dark-layout .rx-item__notes { color: #b4b7bd; }
.dark-layout .rx-results, .dark-layout .rx-result { background: #283046; border-color: #3b4253; }
.dark-layout .rx-result:hover { background: #2f3859; }
.dark-layout .rx-result__name { color: #d0d2d6; }
.dark-layout .rx-result__code { background: #161d31; }
</style>
